import React, { useContext, useEffect, useMemo, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Header } from '@/components/molecules/front-tablet/header'
import { Footer } from '@/components/molecules/front-tablet/footer'
import { useHistory } from 'react-router-dom'
import { fetchReservationsAndSales } from '@/apis/aipass'
import { SearchCheckOutReservation } from '@/models/reservation'
import { FrontTabletLayout } from '@/components/layouts/front-tablet-layout'
import { useSelfCheckOutState } from '@/hooks/use-self-check-out-state'
import { FrontTabletCheckbox } from '@/components/molecules/front-tablet/input/checkbox'
import dayjs from 'dayjs'
import { chain, groupBy } from 'lodash'
import { Body } from '@/components/molecules/front-tablet/body'
import { FrontTabletRadioButton } from '@/components/molecules/front-tablet/input/radio-button'
import { LoaderContextCreator } from '@/contexts/loader'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { CheckoutConfirmDialog } from '@/components/organisms/self-checkout/checkout-confirm-dialog'
import { SearchItem } from '@/models/search-setting'

type AssignRoomType = SearchCheckOutReservation['assignRooms'][0]
export const SelectReservation: React.FC = () => {
  const history = useHistory()
  const { t, i18n } = useTranslation()
  const { state, saveState, stateErrorHandling } = useSelfCheckOutState()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { errorHandler } = useErrorHandler()

  const [selectedReservations, setSelectedReservations] = useState<SearchCheckOutReservation[]>([])
  const [selectedRooms, setSelectedRooms] = useState<AssignRoomType[]>([])
  const [selectedCheckinId, setSelectedCheckinId] = useState<string>()
  const [isInitializeComponent, setIsInitializeComponent] = useState(false)
  const [isShowCheckoutConfirm, setIsShowCheckoutConfirm] = useState(false)
  const [hasUnpaidSales, setHasUnpaidSales] = useState(false)

  const submit = async () => {
    try {
      setIsLoading(true)
      const selectedReservationIds = selectedReservations.map(r => r.reservationId) || []
      const selectedGuestRoomAssignIds = selectedRooms.map(sr => sr.guestRoomAssignId) || []
      const usage = await fetchReservationsAndSales({
        hotelId: state.hotelId,
        reservationIds: selectedReservationIds,
        filterGuestRoomAssignIds: selectedGuestRoomAssignIds,
      })

      const isNeedPaymentSelect = !!(state.setting.isEnabledPaymentSetting && usage.totalAmount > 0)
      setHasUnpaidSales(usage.totalAmount > 0)
      const selectedReservationAndRooms = selectedReservations.map(r => ({
        reservationId: r.reservationId,
        guestRoomAssignIds: r.assignRooms
          .filter(r => selectedGuestRoomAssignIds.includes(r.guestRoomAssignId))
          .map(r => r.guestRoomAssignId),
      }))
      saveState({
        ...state,
        selectReservation: {
          selectedReservations,
          selectedReservationIds,
          selectedReservationAndRooms,
          checkinId: selectedCheckinId || '',
          totalAmount: usage.totalAmount,
          payment: isNeedPaymentSelect ? { usage } : undefined,
        },
      })
      if (!isNeedPaymentSelect) {
        setIsShowCheckoutConfirm(true)
        return
      }
      history.push({ pathname: '/self-checkout/payment' })
    } catch (e) {
      errorHandler(e)
    } finally {
      setIsLoading(false)
    }
  }

  const toggleCheckinId = (checkinId: string, roomNumberFilter?: string[]) => {
    setSelectedCheckinId(checkinId)
    const sameCheckinReservations = [...(state.searchReservation?.result.reservations.filter(r => r.checkinId === checkinId) || [])]

    setSelectedReservations(sameCheckinReservations)

    const allAssignRooms = sameCheckinReservations.flatMap(r => r.assignRooms)
    if (allAssignRooms.length === 1) {
      setSelectedRooms(allAssignRooms)
      return
    }
    if (roomNumberFilter) {
      setSelectedRooms(allAssignRooms.filter(room => roomNumberFilter.includes(room.roomNumber)))
    } else {
      setSelectedRooms(allAssignRooms.filter(room => room.guestName === sameCheckinReservations[0].guestName))
    }
  }

  const toggleRoomCheck = (room: AssignRoomType) => {
    const cloneRooms = [...selectedRooms.filter(r => r.guestRoomAssignId !== room.guestRoomAssignId)]
    const doSelect = cloneRooms.length === selectedRooms.length
    if (doSelect) {
      cloneRooms.push(room)
    }
    setSelectedRooms(cloneRooms)
  }

  const computedCheckedRoom = (room: AssignRoomType): boolean => {
    return selectedRooms.some(r => r.guestRoomAssignId === room.guestRoomAssignId)
  }

  const computedRoomInfo = (rooms: SearchCheckOutReservation['assignRooms']) => {
    return Object.values(groupBy(rooms, 'name'))
      .map(groupRoom => {
        if (i18n.language === 'ja') {
          return `${groupRoom[0].name} / ${groupRoom.length}室`
        } else {
          return `${groupRoom[0].nameEn} / ${groupRoom.length}`
        }
      })
      .join('、')
  }

  const isValidSelectedStatus = useMemo(() => {
    return selectedReservations.length !== 0 && selectedRooms.length !== 0
  }, [selectedReservations, selectedRooms])

  const computedSelectableGuestRoom = useMemo(() => {
    return selectedReservations.flatMap(r => r.assignRooms).sort((a, b) => a.roomNumber.localeCompare(b.roomNumber))
  }, [selectedReservations])

  useEffect(() => {
    window.scrollTo(0, 0)
    if (!stateErrorHandling(state, 'searchReservation')) {
      return
    }

    const defaultCheckin = state.searchReservation.result.reservations[0]
    if (state.searchReservation.condition.mode === SearchItem.RoomKey && state.searchReservation.condition.roomNumber) {
      toggleCheckinId(defaultCheckin.checkinId, [state.searchReservation.condition.roomNumber])
    } else {
      toggleCheckinId(defaultCheckin.checkinId)
    }

    setIsInitializeComponent(true)
  }, [])

  if (!isInitializeComponent) {
    return <></>
  }
  return (
    <FrontTabletLayout state={state}>
      <Header goToPreviousPage={history.goBack} title={t('Reservations/Rooms Selection')} />
      <Body subtitle={t('Please select the reservation or room to check out')} style={containerStyle}>
        <div className="selectable-reservation-block">
          <div className="reservation-container">
            <div className="container-title">{t('Reservation Selection')}</div>
            {chain(state.searchReservation?.result.reservations || [])
              .groupBy(r => r.checkinId)
              .map((reservations, checkinId) => (
                <div key={checkinId} className={`selectable-card ${checkinId === selectedCheckinId ? '-selected' : ''}`}>
                  <div className="checkbox-block">
                    <FrontTabletRadioButton
                      id={`radio-${checkinId}`}
                      value={checkinId}
                      name="checkin-id"
                      formValue={selectedCheckinId}
                      onChange={() => toggleCheckinId(checkinId)}
                      radioSizeType="normal"
                      radioLabelSizeType="small"
                    />
                  </div>
                  <div className="text-block">
                    {reservations.map(reservation => (
                      <div className="reservation-card-body" key={reservation.reservationId} onClick={() => toggleCheckinId(checkinId)}>
                        <div className="header">
                          <div>{`${dayjs(reservation.checkinDate).format(t('MM-DD(ddd)'))} ${reservation.nights}${t('Night')}`}</div>
                        </div>
                        <div className="body">
                          <div className="summary">
                            <span>{t('Room')}</span>：<div>{computedRoomInfo(reservation.assignRooms)}</div>
                          </div>
                          <div className="summary">
                            <span>{t('Number of guests')}</span>：
                            <div>
                              {reservation.assignRooms.reduce((p, c) => p + c.pax, 0)}
                              {t('Person')}
                            </div>
                          </div>
                          <div className="summary">
                            <span>{t('Reservation holder')}</span>：<div>{reservation.reservationUserName}</div>
                          </div>
                          <div className="summary">
                            <span>{t('Confirmed guest')}</span>：<div>{reservation.reservationGuestName}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
              .value()}
          </div>
          <div className="guest-room-container">
            <div className="container-title">{t('Rooms Selection')}</div>
            {computedSelectableGuestRoom.map((room, i) => (
              <div
                className={`selectable-card ${computedCheckedRoom(room) ? '-selected' : ''}`}
                key={`room-${i}-${room.roomNumber}`}
                onClick={() => toggleRoomCheck(room)}
              >
                <div className="checkbox-block">
                  <FrontTabletCheckbox value={computedCheckedRoom(room)} />
                </div>
                <div className="text-block">
                  <div className="title">
                    {t('Guest room')} {room.roomNumber}
                  </div>
                  <p>{room.name}</p>
                  <p>
                    {t('Guest')}：{room.guestName}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Body>
      <Footer
        right={{
          isDisabled: !isValidSelectedStatus,
          onClick: submit,
          label: t('Next'),
        }}
      />
      {isShowCheckoutConfirm && (
        <CheckoutConfirmDialog
          message={hasUnpaidSales ? 'Proceed to check-out' : 'No additional charges. Completing check-out.'}
          onCancel={() => setIsShowCheckoutConfirm(false)}
        />
      )}
    </FrontTabletLayout>
  )
}

const selfCheckoutFontSize = {
  // 「予約選択」「客室選択」
  containerTitle: 20,
  // ラジオボタン、チェックボックスのラベル
  header: 20,
  // 「部屋情報」「宿泊人数」...
  title: 20,
  // その他のテキスト
  text: 20,
} as const

const containerStyle = css({
  '.selectable-reservation-block': {
    display: 'flex',
    gap: 32,
    lineHeight: 1.5,
    '.container-title': { fontSize: selfCheckoutFontSize.containerTitle },
    '.header': { fontWeight: 'bold', fontSize: selfCheckoutFontSize.header },
    '.title': { fontSize: selfCheckoutFontSize.title, fontWeight: 'bold' },
    p: { fontSize: selfCheckoutFontSize.text },
    '.summary': { fontSize: selfCheckoutFontSize.text },
    '.body': { paddingTop: 16 },
    '.reservation-container, .guest-room-container': {
      width: '50%',
      '.selectable-card': {
        borderRadius: 10,
        border: '1px solid #fff',
        background: '#fff',
        padding: 32,
        display: 'flex',
        marginTop: 16,
        '.text-block': { flexGrow: 1 },
        '&.-selected': {
          border: '2px solid #f2a40b',
          padding: 31,
          backgroundColor: '#fffde3',
        },
      },
    },
  },
  '.reservation-card-body': {
    paddingTop: 16,
    marginTop: 16,
    borderTop: '1px solid #f2f2f2',
    ':first-of-type': { paddingTop: 0, marginTop: 0, border: 'none' },
    '.body': {
      '.summary': {
        display: 'flex',
        '>span': { minWidth: '6em', width: '6em', display: 'inline-block' },
      },
    },
  },
})
